import {
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Fluid } from 'primeng/fluid';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Tag } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { AdminGame, ProviderStatus, RealmConfig } from '@/lib-backend';
import { RealmService } from '@/app/services/realm.service';
import { RealmGamesService } from '@/app/services/realm-games.service';
import { NotificationService } from '@/app/services/notification.service';
import { providerStatusView } from '@/app/utils/provider-status';
import {
    effectiveDescription,
    effectiveIconUrl,
    effectiveIsActive,
    effectiveIsFeatured,
    effectiveTitle,
    gameKindName,
    gameModeLabel,
} from '@/app/utils/game';
import { resolveGameContentUrl } from '@/app/utils/game-content-url';
import { AddGameWizard } from './games/add-game-wizard';
import { EditGameDialog } from './games/edit-game-dialog';
import { BulkGenerateReviewsDialog } from './games/fake-reviews/bulk-generate-reviews-dialog';
import { ReviewModerationDialog } from './games/fake-reviews/review-moderation-dialog';
import { VariantsDialog } from './games/variants/variants-dialog';

@Component({
    selector: 'app-website-info-games',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Fluid,
        IconField,
        InputIcon,
        InputText,
        Message,
        Tag,
        TableModule,
        Tooltip,
        AddGameWizard,
        EditGameDialog,
        BulkGenerateReviewsDialog,
        ReviewModerationDialog,
        VariantsDialog,
    ],
    templateUrl: './games-tab.html',
})
export class GamesTab {
    readonly realm = input.required<RealmConfig>();
    readonly providerStatus = input.required<ProviderStatus | null>();

    private readonly realmService = inject(RealmService);
    protected readonly gamesService = inject(RealmGamesService);
    private readonly notification = inject(NotificationService);

    protected readonly syncing = signal(false);
    protected readonly syncError = signal<string | null>(null);
    protected readonly syncFlash = signal<'success' | 'danger' | null>(null);
    private syncFlashTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly localStatus = signal<ProviderStatus | null>(null);

    protected readonly searchTerm = signal('');
    protected readonly filteredGames = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        if (!term) return this.gamesService.games();
        return this.gamesService.games().filter((g) => {
            const title = effectiveTitle(g).toLowerCase();
            const desc = (effectiveDescription(g) ?? '').toLowerCase();
            const kind = gameKindName(g).toLowerCase();
            return (
                title.includes(term) ||
                desc.includes(term) ||
                kind.includes(term)
            );
        });
    });

    protected readonly wizardVisible = signal(false);
    protected readonly editDialogVisible = signal(false);
    protected readonly editingGame = signal<AdminGame | null>(null);

    protected readonly variantsDialogVisible = signal(false);
    protected readonly variantsGame = signal<AdminGame | null>(null);

    protected readonly moderationDialogVisible = signal(false);
    protected readonly moderationGame = signal<AdminGame | null>(null);

    protected readonly bulkReviewsDialogVisible = signal(false);

    protected readonly currentStatus = computed<ProviderStatus | null>(
        () => this.localStatus() ?? this.providerStatus(),
    );

    protected readonly showGames = computed(
        () => this.currentStatus() !== ProviderStatus.NotConfigured,
    );

    protected readonly view = computed(() =>
        providerStatusView(this.currentStatus()),
    );

    protected readonly providerStatusView = providerStatusView;
    protected readonly effectiveTitle = effectiveTitle;
    protected readonly effectiveDescription = effectiveDescription;
    protected readonly effectiveIconUrl = effectiveIconUrl;
    protected readonly effectiveIsActive = effectiveIsActive;
    protected readonly effectiveIsFeatured = effectiveIsFeatured;
    protected readonly gameKindName = gameKindName;
    protected readonly resolveGameContentUrl = resolveGameContentUrl;
    protected readonly gameModeLabel = gameModeLabel;

    constructor() {
        effect(() => {
            const r = this.realm();
            if (r) {
                this.gamesService.loadGames(r.RealmId);
            } else {
                this.gamesService.clear();
            }
        });
    }

    protected refreshGames(): void {
        this.gamesService.loadGames(this.realm().RealmId);
    }

    protected trackById(_index: number, game: AdminGame): string {
        return game.Id;
    }

    protected openAdd(): void {
        this.wizardVisible.set(true);
    }

    protected openEdit(g: AdminGame): void {
        this.editingGame.set(g);
        this.editDialogVisible.set(true);
    }

    protected openVariants(g: AdminGame): void {
        this.variantsGame.set(g);
        this.variantsDialogVisible.set(true);
    }

    protected openModeration(g: AdminGame): void {
        this.moderationGame.set(g);
        this.moderationDialogVisible.set(true);
    }

    protected openBulkReviews(): void {
        this.bulkReviewsDialogVisible.set(true);
    }

    protected async sync(): Promise<void> {
        if (this.syncing()) return;
        this.syncing.set(true);
        this.syncError.set(null);
        try {
            const result = await this.realmService.syncProvider(
                this.realm().RealmId,
            );
            if (result.kind === 'ok') {
                this.localStatus.set(result.providerStatus);
                this.notification.show({
                    severity: 'success',
                    summary: 'Synchronization completed',
                });
                this.gamesService.loadGames(this.realm().RealmId);
                this.flashSync('success');
            } else {
                this.syncError.set(
                    result.reason === 'NotFound'
                        ? 'Realm not found on the platform.'
                        : 'Sync failed. Please try again.',
                );
                this.flashSync('danger');
            }
        } catch (e) {
            this.flashSync('danger');
            throw e;
        } finally {
            this.syncing.set(false);
        }
    }

    private flashSync(state: 'success' | 'danger'): void {
        this.syncFlash.set(state);
        if (this.syncFlashTimer) clearTimeout(this.syncFlashTimer);
        this.syncFlashTimer = setTimeout(() => {
            this.syncFlash.set(null);
            this.syncFlashTimer = null;
        }, 1500);
    }
}
